import { useActions } from "@/hooks/useActions"
import { useTypeSelector } from "@/hooks/useTypeSelector"
import React, { useState, useRef } from "react"
import Button from "react-bootstrap/Button"
import Modal from "react-bootstrap/Modal"
// import Cropper from "react-easy-crop"
import Cropper, { ReactCropperElement } from "react-cropper"
import { IoClose } from "react-icons/io5"

import "cropperjs/dist/cropper.css"

type Props = {
}

const MAX_IMAGE_SIZE = 1920; // Maximum dimension for high quality display
const MIN_IMAGE_SIZE = 640;  // Minimum dimension to ensure quality

const getOptimalDimensions = (width: number, height: number) => {
  const aspectRatio = width / height;
  let newWidth = width;
  let newHeight = height;

  // Only resize if image is larger than MAX_IMAGE_SIZE
  if (width > MAX_IMAGE_SIZE || height > MAX_IMAGE_SIZE) {
    if (width > height) {
      newWidth = MAX_IMAGE_SIZE;
      newHeight = Math.round(MAX_IMAGE_SIZE / aspectRatio);
    } else {
      newHeight = MAX_IMAGE_SIZE;
      newWidth = Math.round(MAX_IMAGE_SIZE * aspectRatio);
    }
  }

  // Ensure minimum size
  if (newWidth < MIN_IMAGE_SIZE) {
    newWidth = MIN_IMAGE_SIZE;
    newHeight = Math.round(MIN_IMAGE_SIZE / aspectRatio);
  }
  if (newHeight < MIN_IMAGE_SIZE) {
    newHeight = MIN_IMAGE_SIZE;
    newWidth = Math.round(MIN_IMAGE_SIZE * aspectRatio);
  }

  return { width: newWidth, height: newHeight };
};

const resizeImage = (imageUrl: string): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d', { 
        willReadFrequently: true,
        colorSpace: 'srgb'
      });
      
      if (!ctx) {
        reject(new Error('Could not get canvas context'));
        return;
      }

      // Get optimal dimensions
      const { width, height } = getOptimalDimensions(img.width, img.height);

      // Set canvas size
      canvas.width = width;
      canvas.height = height;

      // Use high quality image scaling
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';

      // For large downscaling, use a two-step process
      if (width < img.width / 2 || height < img.height / 2) {
        // First step: resize to intermediate size
        const tempCanvas = document.createElement('canvas');
        const tempCtx = tempCanvas.getContext('2d', { 
          willReadFrequently: true,
          colorSpace: 'srgb'
        });
        
        if (!tempCtx) {
          reject(new Error('Could not get temporary canvas context'));
          return;
        }

        // Calculate intermediate size (halfway between original and target)
        const intermediateWidth = Math.ceil((img.width + width) / 2);
        const intermediateHeight = Math.ceil((img.height + height) / 2);

        tempCanvas.width = intermediateWidth;
        tempCanvas.height = intermediateHeight;

        tempCtx.imageSmoothingEnabled = true;
        tempCtx.imageSmoothingQuality = 'high';
        tempCtx.drawImage(img, 0, 0, intermediateWidth, intermediateHeight);

        // Second step: resize to final size
        ctx.drawImage(tempCanvas, 0, 0, width, height);
      } else {
        // Direct resize for smaller scaling
        ctx.drawImage(img, 0, 0, width, height);
      }

      // Convert to data URL using PNG for lossless quality
      const resizedImageUrl = canvas.toDataURL('image/png');
      resolve(resizedImageUrl);
    };
    img.onerror = reject;
    img.src = imageUrl;
  });
};

const CropModal = () => {
  const cropperRef = useRef<ReactCropperElement>(null)
  const [aspectRatio, setAspectRatio] = useState<number>(16 / 9)
  const { toggleCropModalShow, setCroppedImagePath } = useActions()
  const image = useTypeSelector((state) => state.qr.image)

  const { cropModalShow,type } = useTypeSelector((state) => state.qr)

  const onCloseModal = () => {
    toggleCropModalShow(type)
  }
  const onSave = async () => {
    if (image?.path && type!=="Crop") {
      try {
        // Resize the image before setting it
        const resizedImageUrl = await resizeImage(image.path);
        setCroppedImagePath(resizedImageUrl);
        onCloseModal();
      } catch (error) {
        console.error('Error resizing image:', error);
        setCroppedImagePath(image.path);
        onCloseModal();
      }
    } else {
      const cropper = cropperRef.current?.cropper
      console.debug("Cropper data", cropper)
      if (typeof cropper !== "undefined") {
        const croppedImageUrl = cropper.getCroppedCanvas().toDataURL()
        try {
          // Resize the cropped image
          const resizedImageUrl = await resizeImage(croppedImageUrl);
          setCroppedImagePath(resizedImageUrl);
          onCloseModal();
        } catch (error) {
          console.error('Error resizing cropped image:', error);
          setCroppedImagePath(croppedImageUrl);
          onCloseModal();
        }
      }
    }
  }

  // Handle image loading to calculate aspect ratio
  const onImageLoaded = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget
    const calculatedAspect = img.naturalWidth / img.naturalHeight
    // setAspectRatio(calculatedAspect)
    console.info("Calculate aspect", calculatedAspect)
  }
  return (
    <Modal
      size="xl"
      onHide={onCloseModal}
      show={cropModalShow}
      centered
      backdrop="static"
      keyboard={false}
      // className="qr-crop-modal"
      className="d-flex flex-row justify-space-between"
    >
      <Modal.Header>
        <Modal.Title className="mt-2 mb-2 ms-3 me-2" style={{ width: "100%" }}>
          <div
            className="d-flex flex-row justify-content-between"
            style={{ width: "100%" }}
          >
            <div>Crop Image</div>{" "}
            <div style={{ cursor: "pointer" }}>
              <IoClose onClick={onCloseModal} />
            </div>
          </div>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body
        style={{
          // backgroundColor: "red",
          
          maxHeight: "70vh",
          overflow: "hidden",
          display: "flex",
          padding:type!=="upload"?"0px":"10px",
          justifyContent: "center",
          height:
            window.innerWidth > 1279
              ? "648px"
              : window.innerWidth > 991
              ? "415px"
              : "150px",
        }}
      >
        {image?.path && (
          <>
            <img
              src={image?.path}
              alt="Source"
              onLoad={onImageLoaded}
              style={{display:type!=="Upload"?"none":"block"}}
            />
            {type!=="Upload"?<Cropper
              ref={cropperRef}
              style={{ height: "100%", width: "100%", maxHeight: "600px" }}
              // zoomTo={0.5}
              initialAspectRatio={aspectRatio}
              preview=".img-preview"
              src={image?.path}
              viewMode={2} // Ensures the image cannot move out of canvas
              autoCropArea={1} // Controls the cropping area size
              restore={false}
              minCropBoxHeight={10}
              minCropBoxWidth={10}
              responsive={false}
              cropBoxMovable={true}
              cropBoxResizable={true}
              checkOrientation={false}
              guides={true}
            />:null}
          </>
        )}
      </Modal.Body>
      <Modal.Footer style={{ display: "flex", justifyContent: "center" }}>
        <Button onClick={onCloseModal} variant="light">
          Cancel
        </Button>
        <Button onClick={onSave} variant="dark">
          Save
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default CropModal
